import './Destination.scss';
import logo from '../../assets/icons/alpha_taxis_logo.svg';
import Button from '../../common/Button/Button';
import Text from '../../common/Text/Text';
import Input from '../../common/Input/Input';
import { ChangeEvent, useDeferredValue, useEffect, useState } from 'react';
import TomTom from '../../api/controllers/TomTom/TomTom';
import { TomTomApiResult } from '../../types/TomTom/TomTomApiResult';
import DestinationTile from './DestinationTile/DestinationTile';
import useBooking from '../../hooks/useBooking/useBooking';
import { useNavigate } from 'react-router';
import { isValidAddressSearch } from '../../utils/address/address';

const formatDestination = (d?: TomTomApiResult): string => {
  if (!d) return '';
  return d.type === 'POI'
    ? `${d.poi?.name}, ${d.address.freeformAddress}`
    : d.address.freeformAddress;
};

const Destination = () => {
  const { destination, setDestination } = useBooking();
  const [search, setSearch] = useState<string>(() => formatDestination(destination));
  const [error, setError] = useState<string>('');
  const [results, setResults] = useState<TomTomApiResult[]>([]);
  const deferredSearch = useDeferredValue(search);
  const navigate = useNavigate();

  useEffect(() => {
    if (!deferredSearch || !isValidAddressSearch(deferredSearch)) return;

    const abort = new AbortController();
    TomTom.fuzzySearch(deferredSearch, abort.signal)
      .then(res => setResults(res.results))
      .catch(() => {
        if (abort.signal.aborted) return;
        setError('We could not search for that address. Please try again.');
      });

    return () => {
      abort.abort();
    };
  }, [deferredSearch]);

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === '') {
      setResults([]);
    }
    setSearch(value);
    setError(value && !isValidAddressSearch(value) ? 'You must enter a valid address' : '');
    setDestination(undefined);
  };

  const handleResult = (v: TomTomApiResult) => {
    setSearch(formatDestination(v));
    setDestination(v);
  };

  const handleContinue = () => {
    navigate('/passengers') as void;
  };

  return (
    <div className="destination">
      <img src={logo} className="logo" />
      <div className="content">
        <Text type="header">Where are you going?</Text>
        <Input inputId="destination" value={search} onChange={handleSearchChange} error={error} />
        <Button
          buttonStyleType="primary"
          label="Continue"
          handleClick={handleContinue}
          customStyling={{ width: '100%' }}
          disabled={!destination}
        />
        <hr />
      </div>
      {results.length === 0 || error ? null : (
        <div className="results">
          {results.map(destination => (
            <DestinationTile
              key={destination.id}
              destination={destination}
              onClick={handleResult}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Destination;
